import venueDashboardApiService from './venueDashboardApi';
import { resolveBookingAmounts } from '../utils/bookingAmounts';
import { resolveBookingActivity } from '../utils/bookingDisplay';
import { normalizeBookingIdentity } from '../utils/bookingPayment';
import { API_BASE_URL } from '../config/apiEndpoints';

const getAuthHeaders = () => {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const parseBookingsResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.bookings)) return responseData.bookings;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.items)) return responseData.items;
  return [];
};

const guestEmailForBooking = (booking) => {
  const req = booking?.requirements || {};
  const parsedReq =
    typeof req === 'string'
      ? (() => {
          try {
            return JSON.parse(req);
          } catch {
            return {};
          }
        })()
      : req;
  const ci = parsedReq?.customer_info || parsedReq?.customerInfo || {};
  return normalizeBookingIdentity(
    ci.email || booking.contact_email || booking.contactEmail || booking.customer_email || ''
  );
};

const dedupeBookings = (bookings) => {
  const seen = new Set();
  return bookings.filter((booking) => {
    const id = booking?.id;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const transformBooking = (booking) => {
  const amounts = resolveBookingAmounts(booking, {
    venueId: booking.venue_id || booking.venueId || ''
  });
  return {
    id: booking.id,
    bookingReference: booking.booking_reference || booking.bookingReference || '',
    venueName: booking.venue?.name || booking.venue_name || 'Unknown venue',
    date: booking.booking_date || booking.date || '',
    startTime: booking.start_time || booking.startTime || '',
    endTime: booking.end_time || booking.endTime || '',
    status: booking.status || 'pending',
    paymentStatus: booking.payment_status || booking.paymentStatus || 'pending',
    totalPrice: amounts.grandTotal,
    amountPending: amounts.amountPending,
    venueId: booking.venue_id || booking.venueId || '',
    activity: resolveBookingActivity(booking),
    contactName: booking.contact_name || booking.customer_name || '',
    contactEmail:
      guestEmailForBooking(booking) ||
      normalizeBookingIdentity(booking.contact_email || booking.customer_email || ''),
    customerId: booking.customer_id || booking.customerId || null,
    eventOrganizerId: booking.event_organizer_id || booking.eventOrganizerId || null
  };
};

const fetchBookingsByQuery = async (queryString) => {
  const response = await fetch(`${API_BASE_URL}/bookings?${queryString}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) return [];
  const payload = await response.json();
  return parseBookingsResponse(payload);
};

export const fetchOrganizerBookingsByUserId = async (userId) => {
  if (!userId) return [];

  const paramNames = ['customer_id', 'event_organizer_id'];
  let allBookings = [];

  for (const paramName of paramNames) {
    try {
      const bookings = await fetchBookingsByQuery(`${paramName}=${encodeURIComponent(String(userId))}`);
      if (bookings.length > 0) {
        allBookings.push(...bookings);
      }
    } catch {
      // try next param
    }
  }

  return dedupeBookings(allBookings).map(transformBooking);
};

export const fetchOrganizerBookingsByEmail = async (email) => {
  const normalizedEmail = normalizeBookingIdentity(email);
  if (!normalizedEmail) return [];

  const queryParams = [
    `contact_email=${encodeURIComponent(normalizedEmail)}`,
    `customer_email=${encodeURIComponent(normalizedEmail)}`,
    `organizer_email=${encodeURIComponent(normalizedEmail)}`
  ];

  let allBookings = [];
  for (const query of queryParams) {
    try {
      const bookings = await fetchBookingsByQuery(query);
      if (bookings.length > 0) {
        allBookings.push(...bookings);
      }
    } catch {
      // try next param
    }
  }

  const filtered = dedupeBookings(allBookings).filter(
    (booking) => guestEmailForBooking(booking) === normalizedEmail
  );
  return filtered.map(transformBooking);
};

export const resolveOrganizerProfile = async ({ userId, email } = {}) => {
  const trimmedUserId = String(userId || '').trim();
  const trimmedEmail = String(email || '').trim();

  if (trimmedUserId) {
    const user = await venueDashboardApiService.getUserById(trimmedUserId);
    const bookings = await fetchOrganizerBookingsByUserId(trimmedUserId);
    return {
      viewKey: trimmedUserId,
      user: user || {
        id: trimmedUserId,
        email: bookings[0]?.contactEmail || trimmedEmail || '',
        first_name: bookings[0]?.contactName?.split(' ')?.[0] || '',
        last_name: bookings[0]?.contactName?.split(' ')?.slice(1).join(' ') || ''
      },
      bookings
    };
  }

  if (trimmedEmail) {
    const bookings = await fetchOrganizerBookingsByEmail(trimmedEmail);
    const resolvedUserId =
      bookings.find((booking) => booking.customerId)?.customerId ||
      bookings.find((booking) => booking.eventOrganizerId)?.eventOrganizerId ||
      null;
    const user = resolvedUserId
      ? await venueDashboardApiService.getUserById(resolvedUserId)
      : {
          id: null,
          email: trimmedEmail,
          first_name: bookings[0]?.contactName?.split(' ')?.[0] || '',
          last_name: bookings[0]?.contactName?.split(' ')?.slice(1).join(' ') || ''
        };

    return {
      viewKey: resolvedUserId || trimmedEmail,
      user: user || { id: null, email: trimmedEmail },
      bookings
    };
  }

  return { viewKey: null, user: null, bookings: [] };
};
